<template>
  <div
    class="w-100 d-flex flex-wrap align-items-center mb-auto"
    :class="{ 'mt-auto': filter.type === 'checkbox' }">
    <label
      v-if="filter.label && !hasOwnLabel"
      class="w-100"
      :for="`filter-${filter.key}`">
      {{ filter.label }}
    </label>

    <div
      class="flex-grow-1"
      style="min-width: 0;">
      <validation-provider
        :rules="resolvedRules"
        :name="filter.label"
        v-slot="{ errors }">

        <!-- Text/Number Input -->
        <d-input
          v-if="isInputType"
          :id="`filter-${filter.key}`"
          v-model="internalValue"
          :type="filter.type"
          :min="filter.min"
          :max="filter.max"
          :placeholder="filter.placeholder"
          :disabled="disabled"
          :state="errors.length ? 'invalid' : null" />

        <!-- Select -->
        <div
          v-else-if="filter.type === 'select'"
          :class="{
            'form-control is-select': true,
            'is-invalid': errors.length,
            'is-loading': filter.loading
          }">
          <v-select
            :id="`filter-${filter.key}`"
            v-model="internalValue"
            :options="filter.options"
            :placeholder="filter.placeholder"
            :searchable="filter.searchable !== false"
            :multiple="filter.multiple === true"
            :reduce="filter.reduce || (option => option.value)"
            :disabled="disabled"
            :filter="filter.filterMethod"
            :label="filter.optionLabel || 'text'">
            <template slot="no-options">
              {{ $t('ui.noResults') }}&hellip;
            </template>
            <template
              slot="option"
              slot-scope="option">
              <div v-pure-html="option[filter.optionLabel || 'text']"></div>
            </template>
            <template
              slot="selected-option"
              slot-scope="option">
              <div v-pure-html="option[filter.optionLabel || 'text']"></div>
            </template>
          </v-select>
        </div>

        <!-- Date Range -->
        <div
          v-else-if="filter.type === 'daterange'"
          class="d-flex align-items-center w-100"
          :class="{ 'is-invalid': errors.length }">
          <date-range-picker
            :id="`filter-${filter.key}`"
            v-model="internalValue"
            :locale-data="dateRangeLocaleData"
            :autoApply="true"
            :ranges="false"
            :linkedCalendars="true"
            class="flex-grow-1"
            :class="{ 'picker-disabled': disabled }"
            :disabled="disabled">
            <template v-slot:input="picker">
              <span v-if="picker.startDate && picker.endDate">
                {{ formatDate(picker.startDate) }} - {{ formatDate(picker.endDate) }}
              </span>
              <span
                v-else
                class="text-muted">{{ filter.placeholder || $t('ui.chooseDateRange') }}</span>
            </template>
          </date-range-picker>
          <button
            v-if="hasDates && !disabled && filter.clearable !== false"
            type="button"
            class="btn btn-link text-danger p-0 ml-1"
            :title="$t('ui.resetFilters')"
            @click.prevent="clearDateRange">
            <i
              class="material-icons"
              style="font-size: 18px;">close</i>
          </button>
        </div>

        <!-- Toggle Button Group -->
        <d-button-group
          v-else-if="filter.type === 'toggle'"
          class="w-100">
          <d-button
            v-for="option in filter.options"
            :key="option.value"
            :outline="internalValue !== option.value"
            :disabled="disabled"
            @click.prevent="internalValue = option.value">
            {{ option.text }}
          </d-button>
        </d-button-group>

        <!-- Checkbox -->
        <d-checkbox
          v-else-if="filter.type === 'checkbox'"
          :id="`filter-${filter.key}`"
          v-model="internalValue"
          toggle
          toggle-small
          :disabled="disabled">
          {{ filter.checkboxLabel || filter.label }}
        </d-checkbox>

        <!-- Customer Select -->
        <select-customer
          v-else-if="filter.type === 'customerSelect'"
          ref="customerSelect"
          :customerID="internalValue"
          :customerType="filter.customerType"
          class="w-100"
          :disabled="disabled"
          @change="onEntitySelectChange($event, 'customerSelect', 'customers')" />

        <!-- Agent Select -->
        <select-agent
          v-else-if="filter.type === 'agentSelect'"
          ref="agentSelect"
          :agentID="internalValue"
          class="w-100"
          :disabled="disabled"
          @change="onEntitySelectChange($event, 'agentSelect', 'agents')" />

        <!-- User Select -->
        <select-user
          v-else-if="filter.type === 'userSelect'"
          ref="userSelect"
          :userID="internalValue"
          class="w-100"
          :disabled="disabled"
          @change="onEntitySelectChange($event, 'userSelect', 'users')" />

        <!-- Month picker -->
        <d-datepicker
          v-else-if="filter.type === 'monthPicker'"
          v-model="internalValue"
          :monday-first="true"
          format="MMMM yyyy"
          :placeholder="filter.placeholder"
          minimum-view="month"
          class="w-100"
          :disabled="disabled"
          :language="translationsForDatePicker" />
        <div
          v-if="errors.length"
          class="invalid-feedback"
          style="display: block;">
          {{ errors[0] }}
        </div>
      </validation-provider>
    </div>

    <!-- Tooltip with description -->
    <i
      v-if="filter.description"
      :id="`filter-tooltip-${filter.key}`"
      class="material-icons text-info ml-1 cursor-help">
      info
    </i>

    <d-tooltip
      v-if="filter.description"
      :target="`#filter-tooltip-${filter.key}`">
      {{ filter.description }}
    </d-tooltip>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import SelectCustomer from '@/components/common/SelectCustomer.vue';
import SelectAgent from '@/components/common/SelectAgent.vue';
import SelectUser from '@/components/common/SelectUser.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';
import datepickerTranslations from '@/data/datepicker-translations.js';
import getPrefferedLanguage from '@/utils/i18nUtils.js';

export default {
  name: 'filter-control',
  components: {
    'date-range-picker': DateRangePicker,
    'select-customer': SelectCustomer,
    'select-agent': SelectAgent,
    'select-user': SelectUser
  },
  props: {
    disabled: {
      default: false,
      type: Boolean
    },
    filter: {
      required: true,
      type: Object
    },
    allFilters: {
      default: () => ({}),
      type: Object
    },
    value: {
      required: true
    }
  },
  computed: {
    internalValue: {
      get () {
        return this.value;
      },
      set (val) {
        this.$emit('input', val);
      }
    },
    isInputType () {
      return this.filter.type === 'text' || this.filter.type === 'number';
    },
    hasOwnLabel () {
      return this.filter.isBasic || this.filter.type === 'checkbox' || this.filter.type === 'toggle';
    },
    hasDates () {
      if (this.filter.type !== 'daterange') {
        return false;
      }
      const val = this.internalValue;
      return val && (val.startDate || val.endDate);
    },
    dateRangeLocaleData () {
      return CalendarUtils.getDateRangePickerLocaleData(this.$t.bind(this));
    },
    resolvedRules () {
      if (!this.filter.rules) {
        return null;
      }
      if (typeof this.filter.rules === 'function') {
        return this.filter.rules(this.allFilters);
      }
      return this.filter.rules;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    entitySelectMapping () {
      const map = {
        customerSelect: { ref: 'customerSelect', data: 'customers' },
        agentSelect: { ref: 'agentSelect', data: 'agents' },
        userSelect: { ref: 'userSelect', data: 'users' }
      };
      return map[this.filter.type] || null;
    }
  },
  mounted () {
    const mapping = this.entitySelectMapping;
    if (mapping && this.value) {
      const unwatch = this.$watch(
        () => {
          const comp = this.$refs[mapping.ref];
          return comp ? comp[mapping.data] : [];
        },
        (items) => {
          if (items && items.length) {
            const found = items.find(item => item.id === this.value);
            if (found) {
              this.$emit('display-label', found.name);
              unwatch();
            }
          }
        },
        { immediate: true }
      );
    }
  },
  methods: {
    formatDate (date) {
      if (!date) {
        return '-';
      }
      return CalendarUtils.formatDateForPHP(new Date(date));
    },
    clearDateRange () {
      this.internalValue = { startDate: null, endDate: null };
    },
    onEntitySelectChange (id, refName, dataKey) {
      this.internalValue = id;
      let label = '';
      if (id && this.$refs[refName]) {
        const items = this.$refs[refName][dataKey] || [];
        const found = items.find(item => item.id === id);
        if (found) {
          label = found.name;
        }
      }
      this.$emit('display-label', label);
    }
  }
};
</script>
